import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import type { Company } from '@/types/database';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Check, 
  Crown,
  Loader2,
  Lock,
  X
} from 'lucide-react';
import { StorageImage } from '@/components/ui/StorageImage';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { BulkActionBar } from '@/components/bulk-actions/BulkActionBar';
import { useNewCompanyAction } from '@/components/company/useNewCompanyAction';

interface CompanyListProps {
  initialAction?: 'new' | null;
  /**
   * Cabeçalho próprio ("Minhas empresas" + contagem do plano + "Nova empresa").
   *
   * A tela de Configurações passa `false`: no desenho oficial a contagem e o
   * limite do plano são o VALOR AO VIVO do hero e a criação é a ação primária
   * dele. Mantido aqui dentro por padrão porque o cabeçalho é o que dá contexto
   * quando a lista é usada fora de um hero.
   */
  showHeader?: boolean;
}

export function CompanyList({ initialAction, showHeader = true }: CompanyListProps) {
  const { companies, activeCompany, setActiveCompanyId, deleteCompany, loading } = useCompany();
  const multipleCompanies = useFeature('multiple_companies');
  const { startNewCompany } = useNewCompanyAction();
  const navigate = useNavigate();

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Bulk selection hook
  const {
    selectedIds,
    selectedCount,
    allSelected,
    isSelected,
    toggleItem,
    toggleSelectAll,
    clearSelection,
  } = useBulkSelection(companies);

  // O gate de plano mora no hook (ver `useNewCompanyAction`): com o botão
  // primário no hero das Configurações, esta regra passou a ter mais de um
  // call-site e não pode ser reescrita em cada um.
  const handleAddClick = startNewCompany;

  const handleEditClick = (company: Company) => {
    navigate(`/companies/${company.id}/edit`);
  };

  // ?action=new (link vindo de outra tela) abre o formulário direto.
  useEffect(() => {
    if (initialAction === 'new') handleAddClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAction]);

  const handleDeleteClick = (companyId: string) => {
    setDeletingId(companyId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    await deleteCompany(deletingId);
    setIsDeleting(false);
    setShowDeleteDialog(false);
    setDeletingId(null);
  };

  const handleSetActive = (companyId: string) => {
    if (companyId !== activeCompany?.id && !multipleCompanies.require()) return;
    setActiveCompanyId(companyId);
  };

  const handleBulkDelete = async () => {
    if (selectedCount === 0) return;
    
    // Don't allow deleting all companies
    if (selectedCount >= companies.length) {
      setShowBulkDeleteDialog(false);
      return;
    }
    
    setIsBulkDeleting(true);
    
    // Delete each selected company
    for (const id of selectedIds) {
      await deleteCompany(id);
    }
    
    setIsBulkDeleting(false);
    setShowBulkDeleteDialog(false);
    clearSelection();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header — flex-wrap para não estourar em 360px (o título+subtítulo
          não tinha shrink/truncate e a linha não quebrava).

          Some quando quem hospeda a lista já diz as mesmas duas coisas: nas
          Configurações a contagem e o limite do plano são o valor ao vivo do
          hero, e "Nova empresa" é a ação primária dele. Repetir os dois a dois
          centímetros de distância é a duplicação que o #200 removeu do
          Financeiro. */}
      {showHeader && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Minhas empresas</h2>
            <p className="text-sm text-muted-foreground">
              {multipleCompanies.allowed
                ? 'Gerencie suas identidades comerciais'
                : `${companies.length}/1 empresa (Plano gratuito)`}
            </p>
          </div>
          <Button3D onClick={handleAddClick} size="sm" className="shrink-0 gap-2" data-tour="tour-add-company">
            <Plus className="h-4 w-4" />
            Nova empresa
          </Button3D>
        </div>
      )}

      {/* Company Cards */}
      {companies.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Building2 className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              Nenhuma empresa cadastrada
            </p>
            <Button3D onClick={handleAddClick} className="mt-4 gap-2">
              <Plus className="h-4 w-4" />
              Criar primeira empresa
            </Button3D>
          </CardContent>
        </Card>
      ) : (
        // `grid-cols-1` NÃO é decoração: sem ele a coluna é `auto`, e um track
        // `auto` cresce até o MIN-CONTENT do item mais largo. Medido no aparelho:
        // uma empresa de nome longo ("68.129.625 JOAQUIM RODRIGUES DA COSTA") tem
        // min-content de 353px, então TODOS os cards viravam 353 num container de
        // 328 e a página inteira ia a 369px. O `truncate` do nome não protege:
        // `white-space: nowrap` faz o min-content ser o texto INTEIRO. O
        // `grid-cols-1` do Tailwind é `repeat(1, minmax(0,1fr))` — o `0` é o que
        // libera o track a encolher.
        <div className="grid grid-cols-1 gap-3">
          {companies.map((company) => {
            const isActive = company.id === activeCompany?.id;
            const checked = isSelected(company.id);
            
            return (
              <Card
                key={company.id}
                className={`transition-all ${isActive ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : ''}`}
              >
                {/* On phones the row of logo + info + 3 actions can't fit in a
                 * ~343px wide card, so actions wrap onto a second row underneath.
                 * `flex-wrap` + `basis-full` on the actions block does exactly
                 * that below the `sm:` breakpoint. */}
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Checkbox for bulk selection */}
                    {companies.length > 1 && (
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleItem(company.id)}
                        aria-label={`Selecionar ${company.name}`}
                        className="shrink-0"
                      />
                    )}

                    {/* Logo */}
                    <div
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl overflow-hidden sm:h-14 sm:w-14"
                      style={{ backgroundColor: company.primary_color + '20' }}
                    >
                      {company.logo_url ? (
                        <StorageImage
                          src={company.logo_url}
                          alt={company.name}
                          className="h-full w-full object-cover"
                          fallback={
                            <Building2
                              className="h-6 w-6"
                              style={{ color: company.primary_color }}
                            />
                          }
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <Building2
                          className="h-6 w-6"
                          style={{ color: company.primary_color }}
                        />
                      )}
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        {/* Sem `truncate`: o nome é o que DISTINGUE uma empresa da
                            outra, e cortá-lo faz duas linhas parecerem iguais (#175).
                            O card só cresce quando o nome é grande — que é quando a
                            informação importa. `min-w-0` é obrigatório ao trocar
                            corte por quebra: um flex item nasce com `min-width:auto`
                            e uma palavra indivisível estica a linha. */}
                        <h3 className="min-w-0 break-words font-semibold">{company.name}</h3>
                        {isActive && (
                          <Badge className="shrink-0 gap-1 bg-primary text-primary-foreground">
                            <Check className="h-3 w-3" />
                            Ativa
                          </Badge>
                        )}
                      </div>
                      {company.cnpj_cpf && (
                        <p className="truncate text-sm text-muted-foreground">
                          {company.cnpj_cpf}
                        </p>
                      )}
                      {company.email && (
                        <p className="truncate text-sm text-muted-foreground">
                          {company.email}
                        </p>
                      )}
                    </div>

                    {/* Actions — wraps to its own row on phones (basis-full)
                     * and sits inline on ≥sm screens. */}
                    <div className="flex basis-full items-center justify-end gap-2 sm:basis-auto">
                      {!isActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetActive(company.id)}
                          className={multipleCompanies.allowed ? "border-primary text-primary hover:bg-primary hover:text-primary-foreground" : "border-amber-500/50 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"}
                        >
                          {!multipleCompanies.allowed && <Lock className="mr-1 h-3.5 w-3.5" />}
                          {multipleCompanies.allowed ? 'Ativar' : 'PRO'}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditClick(company)}
                        className="h-9 w-9"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteClick(company.id)}
                        className="h-9 w-9 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Bulk Action Bar */}
      {companies.length > 1 && (
        <BulkActionBar
          selectedCount={selectedCount}
          totalCount={companies.length}
          allSelected={allSelected}
          onSelectAll={(checked) => toggleSelectAll(checked, companies)}
          onDelete={() => setShowBulkDeleteDialog(true)}
          onClear={clearSelection}
          isDeleting={isBulkDeleting}
          className="bottom-24"
        />
      )}

      {/* PRO Banner for free users */}
      {!multipleCompanies.allowed && companies.length >= 1 && (
        <Card className="border-warning/50 bg-warning/5">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning/20">
              <Crown className="h-5 w-5 text-warning" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm">
                Quer gerenciar mais empresas?
              </p>
              <p className="text-sm text-muted-foreground">
                Upgrade para PRO e tenha identidades ilimitadas!
              </p>
            </div>
            <Button3D
              color="amber"
              size="sm"
              className="shrink-0"
              onClick={() => multipleCompanies.require()}
            >
              <Crown className="mr-1 h-4 w-4" />
              Upgrade
            </Button3D>
          </CardContent>
        </Card>
      )}

      {/* Single Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Os orçamentos associados a esta empresa 
              não serão excluídos, mas ficarão sem vínculo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {selectedCount} {selectedCount === 1 ? 'empresa' : 'empresas'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Os orçamentos associados a estas empresas 
              não serão excluídos, mas ficarão sem vínculo.
              {selectedCount >= companies.length - 1 && (
                <p className="mt-2 text-warning font-medium">
                  ⚠️ Você precisa manter pelo menos uma empresa.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isBulkDeleting || selectedCount >= companies.length}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir selecionadas'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
